// @flow

import React, { Fragment } from "react";
import { BaseHierarchyTree, Icons } from "../index";
import type { TreeBranch } from "../base-hierarchy-tree";
import { TYPE_ORGANIZATION } from "../base-hierarchy-tree/util";
import withSearchable from "../searchable-hierarchy-tree";
import { MenuItem } from "@components";
import "./organization.style.scss";

type Props = $Exact<{
  tree: TreeBranch,
  disabled: boolean,
  expanded: number[],
  excluded?: number[],
  expandAll: boolean,
  hoverEmployee: boolean,
  selectedBranchId?: number | null,
  onAction: (action: string, branch: TreeBranch) => void,
  onDrop: (from: number, to: number) => void,
  onClick?: (branch: TreeBranch) => void,
  hideEmployee: boolean,
  showDisabled: boolean,
  showEmployees: boolean,
  showLeavers: boolean,
  className?: string,
}>;

export class OrganizationHierarchyTree extends React.Component<Props> {
  static defaultProps = {
    disabled: false,
    expanded: [],
    excluded: [],
    selectedBranchId: null,
    onClick: () => undefined,
  };

  getEmployeesCount = (branch: TreeBranch) => {
    const employees = (branch.children || []).filter((child: TreeBranch) => child.data.employeeId);
    const inactiveEmployees = employees.filter(employee => employee.data.isActive === false);
    return {
      total: employees,
      inactive: inactiveEmployees,
    };
  };
  get expandedNodes() {
    return this.props.expanded;
  }

  triggerAction = (action: string, branch: TreeBranch) => () => {
    this.props.onAction(action, branch);
  };

  actions = (branch: TreeBranch) => {
    const employeesCount = this.getEmployeesCount(branch);
    if (this.props.disabled) {
      return <div />;
    }
    if (this.props.tree.data.id === branch.data.id || branch.data.reference === "UNASSIGNED") {
      return (
        <MenuItem className="d-flex align-items-center" onClick={this.triggerAction("add", branch)}>
          <span className="material-icons mr-2">add</span> {"Add Sub-department"}
        </MenuItem>
      );
    }
    return (
      <Fragment>
        <MenuItem
          onClick={this.triggerAction("add", branch)}
          label={"Add Sub-department"}
          icon="add"
        />

        <MenuItem
          className="d-flex align-items-center"
          onClick={this.triggerAction("move", branch)}
          label={"Move"}
          icon="move_up"
        />

        <MenuItem
          onClick={this.triggerAction("rename", branch)}
          className="d-flex align-items-center"
          label={"Edit"}
          icon="edit"
        />

        {employeesCount.total.length < 1 ? (
          <MenuItem
            className="d-flex align-items-center"
            color="negative"
            onClick={this.triggerAction("delete", branch)}
            label={"Delete"}
            icon="delete"
          />
        ) : (
          <MenuItem
            className="text-disabled-light d-flex align-items-center"
            color="text-disabled-light"
            icon="delete"
            label={"Delete"}
          />
        )}
      </Fragment>
    );
  };

  renderControl = (branch: TreeBranch, isHovered: boolean, isInactive: boolean) => {
    if (branch.data.type === TYPE_ORGANIZATION) {
      return !isHovered ? (
        <span className="material-icons text-color-orange pointer pt-1">folder</span>
      ) : (
        <Icons.MoveDraggerIcon className="tree-icon" color="orange" />
      );
    }

    return (
      <span className={`material-icons pointer text-color-${isInactive ? "negative" : "charcoal"}`}>
        person
      </span>
    );
  };

  render() {
    return (
      <BaseHierarchyTree
        className={`organizations-tree ${this.props.className || ""}`}
        tree={this.props.tree}
        draggable={!this.props.disabled}
        actions={this.actions}
        onDrop={this.props.onDrop}
        disabled={false}
        expanded={this.expandedNodes}
        expandAll={this.props.expandAll}
        hoverEmployee={this.props.hoverEmployee}
        excluded={this.props.excluded}
        hasControl={true}
        selectedBranchId={this.props.selectedBranchId}
        onClick={this.props.onClick}
        renderControl={this.renderControl}
        hideEmployee={this.props.hideEmployee}
        showDisabled={this.props.showDisabled}
        showEmployees={this.props.showEmployees}
        showLeavers={this.props.showLeavers}
      />
    );
  }
}
export default withSearchable(OrganizationHierarchyTree);
